import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, {
  createContext,
  useContext,
  useState,
  useId,
  ReactNode,
  useCallback,
} from "react";

// Types
export type DSTabsOrientation = "horizontal" | "vertical";

// Context
interface TabsContextValue {
  value: string;
  onChange: (value: string) => void;
  orientation: DSTabsOrientation;
  generatedId: string;
  dataTestId: string;
  borderColor: string;
}

const TabsContext = createContext<TabsContextValue | null>(null);

const useTabsContext = () => {
  const context = useContext(TabsContext);
  if (!context) {
    throw new Error(
      "DSTabs compound components must be used within DSTabs",
    );
  }
  return context;
};

// Root component props
export interface DSTabsProps {
  /** Controlled value - the currently active tab */
  value?: string;
  /** Initial value for uncontrolled mode */
  defaultValue?: string;
  /** Callback when active tab changes */
  onChange?: (value: string) => void;
  /** Tab list orientation */
  orientation?: DSTabsOrientation;
  /** Border color class for the panel (e.g., "border-brand") */
  borderColor?: string;
  /** Required test ID for testing */
  dataTestId: string;
  children: ReactNode;
}

// Root component
export const DSTabs = ({
  value: controlledValue,
  defaultValue,
  onChange,
  orientation = "horizontal",
  borderColor = "border-brand",
  dataTestId,
  children,
}: DSTabsProps) => {
  const generatedId = useId();
  const [uncontrolledValue, setUncontrolledValue] = useState(
    defaultValue ?? "",
  );

  const isControlled = controlledValue !== undefined;
  const value = isControlled ? controlledValue : uncontrolledValue;

  const handleChange = useCallback(
    (newValue: string) => {
      if (!isControlled) {
        setUncontrolledValue(newValue);
      }
      onChange?.(newValue);
    },
    [isControlled, onChange],
  );

  return (
    <TabsContext.Provider
      value={{
        value,
        onChange: handleChange,
        orientation,
        generatedId,
        dataTestId,
        borderColor,
      }}
    >
      <div
        className={`flex ${
          orientation === "vertical" ? "flex-row gap-md" : "flex-col"
        } w-full`}
        data-testid={dataTestId}
        data-intercom-target={dataTestId}
      >
        {children}
      </div>
    </TabsContext.Provider>
  );
};

// List component props
interface ListProps {
  children: ReactNode;
}

// List component - container for Tab items
const List = ({ children }: ListProps) => {
  const { orientation, dataTestId } = useTabsContext();

  const orientationStyles =
    orientation === "vertical"
      ? "flex-col gap-xs"
      : "flex-row flex-wrap items-end";

  return (
    <div
      role="tablist"
      aria-orientation={orientation}
      className={`flex ${orientationStyles} mx-sm`}
      data-testid={`${dataTestId}-list`}
      data-intercom-target={`${dataTestId}-list`}
    >
      {children}
    </div>
  );
};

// Tab component props
export interface DSTabProps {
  /** Unique identifier for this tab */
  value: string;
  /** Optional badge count to display */
  badge?: number;
  /** Optional leading icon */
  icon?: IconProp;
  /** Custom background color class when active (e.g., "bg-indigo-200 dark:bg-indigo-800") */
  activeColor?: string;
  /** Whether the tab is disabled */
  disabled?: boolean;
  children: ReactNode;
}

// Tab component - individual tab trigger
const Tab = ({
  value: tabValue,
  badge,
  icon,
  activeColor,
  disabled = false,
  children,
}: DSTabProps) => {
  const { value, onChange, orientation, generatedId, dataTestId } =
    useTabsContext();

  const isActive = value === tabValue;
  const tabId = `${generatedId}-tab-${tabValue}`;
  const panelId = `${generatedId}-panel-${tabValue}`;

  const handleClick = () => {
    if (!disabled) {
      onChange(tabValue);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      handleClick();
    }
  };

  // Base styles
  const baseStyles =
    "relative flex items-center justify-center gap-sm font-body transition-all cursor-pointer";

  // Orientation-specific styles
  const getOrientationStyles = () => {
    if (orientation === "vertical") {
      return "px-md py-sm rounded-large w-full justify-start";
    }
    // Horizontal: flex-1 to fill space, rounded top corners, larger padding
    // Active tabs get extra top padding for visual emphasis
    return "flex-1 px-md py-sm rounded-t-large";
  };

  // State styles
  const getStateStyles = () => {
    if (disabled) {
      return "opacity-50 cursor-not-allowed bg-secondary text-placeholder";
    }

    if (isActive) {
      const activeBg = activeColor ?? "bg-brand";
      return `${activeBg} text-white shadow-sm`;
    }

    // Inactive horizontal tabs: subtle background
    if (orientation === "horizontal") {
      return "bg-secondary text-secondary hover:text-primary opacity-80";
    }

    return "bg-secondary text-secondary hover:text-primary hover:bg-grindigo-100 dark:hover:bg-grindigo-900 opacity-80";
  };

  return (
    <button
      role="tab"
      id={tabId}
      aria-selected={isActive}
      aria-controls={panelId}
      aria-disabled={disabled}
      tabIndex={isActive ? 0 : -1}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      disabled={disabled}
      className={`${baseStyles} ${getOrientationStyles()} ${getStateStyles()}`}
      data-testid={`${dataTestId}-tab-${tabValue}`}
      data-intercom-target={`${dataTestId}-tab-${tabValue}`}
    >
      {icon && (
        <FontAwesomeIcon
          icon={icon}
          className="text-sm"
          aria-hidden="true"
        />
      )}
      <span className="text-base font-semibold line-clamp-1">
        {children}
      </span>
      {badge !== undefined && badge > 0 && (
        <span
          className="flex items-center justify-center h-5 min-w-5 px-1 rounded-full bg-danger text-white text-xs font-medium"
          aria-label={`${badge} items`}
        >
          {badge}
        </span>
      )}
    </button>
  );
};

// Panel component props
export interface DSPanelProps {
  /** Must match the value of the corresponding Tab */
  value: string;
  children: ReactNode;
}

// Panel component - content for each tab
const Panel = ({ value: panelValue, children }: DSPanelProps) => {
  const { value, generatedId, dataTestId, borderColor } =
    useTabsContext();

  const isActive = value === panelValue;
  const tabId = `${generatedId}-tab-${panelValue}`;
  const panelId = `${generatedId}-panel-${panelValue}`;

  return (
    <div
      role="tabpanel"
      id={panelId}
      aria-labelledby={tabId}
      tabIndex={isActive ? 0 : -1}
      className={`flex-1 border-t-2 rounded-standard p-md ${borderColor} ${!isActive ? "hidden" : ""}`}
      data-testid={`${dataTestId}-panel-${panelValue}`}
      data-intercom-target={`${dataTestId}-panel-${panelValue}`}
    >
      {children}
    </div>
  );
};

// Attach subcomponents
DSTabs.List = List;
DSTabs.Tab = Tab;
DSTabs.Panel = Panel;

DSTabs.displayName = "DSTabs";
